import { fromJS } from 'immutable';
import { findPropertiesByType, populateMissingSchemaValues } from '@pearsontechnology/aero-plugin-schema';
import { VIEWS } from 'Workspace/components/PreviewDialog/constants';
import AssetManager from 'AssetManager';
import ScopeBindingManager from 'ScopeBindingManager';
import { BOUND_REFERENCES, RESOLVED } from 'ScopeBindingManager/constants';
import { getSchemaVersion } from '../../utils/Schema';
import { ACTIVITY, COMPONENT, INTERACTIVE } from 'services/ActivityService/elementTypes';
import ComponentService from 'services/ComponentService';
import ActivityService from 'services/ActivityService';
import InteractiveService from 'services/InteractiveService';
import { IMAGE_URN_REGEX } from 'containers/Clipboard/helpers/constants';

const URN_EXTERNAL = 'urn:external';
const ASSET = 'asset';
const URN = 'urn';

class ConfigResolver {

  constructor(service, configData) {
    this.service = service;
    this.configData = configData || {};
  }

  _getJS = (data) => data?.toJS ? data.toJS() : data

  getStored = () => AssetManager.getStored()

  resolveAsset = async (urn) => await AssetManager.resolveAsset(urn)

  resolveBoundReferences = async (coursewareId, context, target, originalConfig) => await ScopeBindingManager.resolveBoundReferences(coursewareId, context, target, originalConfig)

  resolvePathwayItems = async (path = [], originalConfig) => {
    const pluginSchema = await this._retrieveCoursewareSchema();
    const pathwayItems = findPropertiesByType(pluginSchema, 'pathway');

    let config;
    if (originalConfig === null || typeof originalConfig === 'undefined') {
      // console.info('here', path);
      config = fromJS(await this.service.getInConfig(this.configData.id, path) || {});
    } else {
      config = fromJS(originalConfig);
    }

    config = populateMissingSchemaValues(pluginSchema, config, path);

    if (pathwayItems.find((schemaItem) => JSON.stringify(schemaItem.path) === JSON.stringify(path))) {
      const pathwayId = config.get('pathwayId');
      const pathway = pathwayId ? await this.service.getPathway(pathwayId) : {};
      pathway.children = this._changeElementPositionOfChildrenPathwaysForPreview(pluginSchema, pathway.children);
      return this._elementRefsToChildRefs(pathway.children);
    }

    if (path.length) {
      return this._getJS(config);
    }

    const pathwayPromises = pathwayItems.map((schemaItem) => {
      const pathwayId = config.getIn([...schemaItem.path, 'pathwayId']);
      return pathwayId ? this.service.getPathway(pathwayId) : {};
    });

    const pathwayResults = await Promise.all(pathwayPromises);

    let updatedConfig = config;
    // console.info('pathwayItems', pathwayItems);
    pathwayItems.forEach((schemaItem, index) => {
      updatedConfig = updatedConfig.setIn(
        [...schemaItem.path],
        this._elementRefsToChildRefs(pathwayResults[index].children)
      );
    });
    return this._getJS(updatedConfig);
  }

  _retrieveCoursewareSchema = async () => {
    if (!this.pluginSchema) {
      const coursewareItem = await this.service.getCoursewareItem(this.configData.id);
      if (coursewareItem) {
        const { plugin } = coursewareItem;
        const { pluginId, version } = plugin;
        const pluginResponse = await this.service.getPlugin(pluginId, version);
        const { manifest } = pluginResponse;
        const { configurationSchema } = manifest;
        this.pluginSchema = JSON.parse(configurationSchema);
      }
    }
    return this.pluginSchema;
  }

  _elementRefsToChildRefs = (elements = []) => elements.map((element) => {
    const { elementId, elementType } = element;
    return {
      itemId: elementId,
      itemType: elementType,
    };
  })

  /**
   * This function changes the position of element in an array for Preview with type = subActivity,
   * For sub-activity if interactive id is matching with the url interactive id then that element
   * will always be as a top element in the array.
   * For non-preview elements/for non-sub-activity elements it returns childrenPathways without any change.
   * @param  pluginSchema
   * @param  childrenPathways
   * @returns childrenPathways
  */
  _changeElementPositionOfChildrenPathwaysForPreview = (pluginSchema, childrenPathways) => {
    const url = window.location.pathname;
    if (url.indexOf(VIEWS.PREVIEW) > -1 && pluginSchema.hasOwnProperty('groupActivityId')) {
      const interactiveId = url.match('screen/') && url.substr(url.lastIndexOf('/') + 1);
      return [...childrenPathways.filter((item) => item.elementId === interactiveId), ...childrenPathways.filter((item) => item.elementId !== interactiveId)];
    }
    return childrenPathways;
  }

  getAssets = (assetData) => {
    const assetMap = AssetManager.getStored();
    return assetData.map((a) => [a, assetMap?.[a]]).filter(([, asset]) => !!asset);
  };

  getConfig = async () => {
    switch (this.configData.type) {
      case COMPONENT:
        return (await ComponentService.getComponent(this.configData.id))?.config || {};
      case ACTIVITY:
        return (await ActivityService.getActivity(this.configData.id))?.config || {};
      case INTERACTIVE:
        return (await InteractiveService.getInteractive(this.configData.id))?.config || {};
      default:
        return {};
    }
  }

  convert = async (value, target, internalAssetFormatter, externalAssetFormatter) => {

    const originalConfig = JSON.stringify(await this.getConfig());
    let assetData = Array.from(new Set(originalConfig.match(IMAGE_URN_REGEX)));
    let replacements = null;

    if (assetData?.length) {
      assetData = this.getAssets(assetData);
      replacements = await Promise.all(assetData.map(async ([urn, asset]) =>
        urn.startsWith(URN_EXTERNAL)
          ? externalAssetFormatter(urn)
          : internalAssetFormatter(urn, asset)
      ));
    }

    // keeping this logic to avoid issues with old courses
    const replaceItem = (doc, [original, replacement]) => {
      // TODO: fix in a pragmatic manner,
      // this is a temporary solution as fromJS changes the shape of asset
      // and config is getting corrupted
      const isReplacementString = typeof replacement === 'string';
      const stringifiedReplacement = isReplacementString ? replacement : JSON.stringify(fromJS(replacement));
      const from = target === ASSET ? original : stringifiedReplacement;
      const to = target === ASSET ? stringifiedReplacement : original;

      return doc?.replace(from, to);
    };


    const asDoc = JSON.stringify(value);
    let replacedDoc = replacements ? replacements.reduce(replaceItem, asDoc) : asDoc;
    let convertedDoc;
    try {
      convertedDoc = JSON.parse(replacedDoc);
    } catch (e) {
      convertedDoc = replacedDoc;
    }
    return convertedDoc;
  };

  isV2AssetSchema = (schemas) => getSchemaVersion(schemas).major >= 2;

  getExternalAssetFormatter = (schemas) =>
    this.isV2AssetSchema(schemas)
      ? async (urn) => {
        const asset = await AssetManager.resolveAsset(urn);
        if (!asset.source.original && asset.source.url) {
          const url = asset.source.url;
          asset.source.original = { url };
          delete asset.source.url;
        }
        return [`"${urn}"`, asset];
      }
      : async (urn) => {
        const asset = await AssetManager.resolveAsset(urn);
        return [urn, asset.url || asset.original.url];
      };

  getInternalAssetFormatter = (schemas) =>
    this.isV2AssetSchema(schemas)
      ? (urn, asset) => [`"${urn}"`, asset]
      : (urn, asset) => [urn, asset.url || asset.original.url];

  /**
   * This function is used to resolve all assets (images) and bounded scopes
   * @param  {Array} args - usually contains 'id', 'path', 'value'
   * @param  {Object} originalConfig
   * @param  {Object} schemas
   * @param  {String} bindingType - 'RESOLVED | BOUNDED_REFERENCES'
   * @returns {Object} resolvedConfig
  */

  resolve = async (id, path, value, originalConfig, schemas, bindingType) => {
    const coursewareId = id ? id : this.configData.id;
    const configWithResolvedPathways = await this.resolvePathwayItems(path, originalConfig);
    /*
      This has been done to handle preview mode. configWithResolvedPathways can be an array of objects like the following
      [{0: {itemId: 'ITEM_ID', itemType: 'INTERACTIVE'}}]. There are no
      bindings that are required to be resolved here and needs to directly returned. For other scenarios
      the configWithResolvedPathways is a simple object
    */
    if (Array.isArray(configWithResolvedPathways) && configWithResolvedPathways.length > 0 && bindingType !== BOUND_REFERENCES) {
      return configWithResolvedPathways;
    }

    let resolvedConfig;
    if (bindingType === RESOLVED) {
      // const pathArgs = schemas && args || [this.configData.id, Array.isArray(args[1]) ? args[1] : args];
      const schemaData = schemas || await this.service.getSchemasIn(coursewareId);
      const resolvedBoundedReferences = await this.resolveBoundReferences(coursewareId, path, RESOLVED, configWithResolvedPathways);
      resolvedConfig = await this.convert(resolvedBoundedReferences, ASSET, this.getInternalAssetFormatter(schemaData), this.getExternalAssetFormatter(schemaData));
    } else if (bindingType === BOUND_REFERENCES) {
      const urnConverted = await this.convert(value, URN, this.getInternalAssetFormatter(schemas), this.getExternalAssetFormatter(schemas));
      resolvedConfig = await this.resolveBoundReferences(coursewareId, path, BOUND_REFERENCES, urnConverted);
    } else {
      resolvedConfig = await this.convert(value, 'urn', this.getInternalAssetFormatter(schemas));
    }

    return resolvedConfig;
  }

}

export default ConfigResolver;
